import { useState, type ReactNode } from 'react';
import { Box, Button, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { NONE } from '@casa/model';
import { GRADIENT_NAMES, cssColor, namedGradient } from '@casa/tiles';
import { SettingsGroup, SettingsRow } from '../settings/settings_ui.tsx';
import { ScreenRow, TextFieldRow } from './form_rows.tsx';

// Colours in a form (dashboard-editor spec T-2): a row with a swatch, opening a screen with the
// colours this dashboard already uses, a palette, and a colour of one's own as #rrggbb; and the
// gauges' gradients, by name with a strip of each, or a list of one's own.

/** A colour as the dashboard takes it (`ColorSchema`): `#rgb`, `#rrggbb` or `#aarrggbb`. */
const COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** The palette: the legacy tile's own colours first, then plain ones. */
export const PALETTE: readonly { color: string; name: string }[] = [
    { color: '#151515', name: 'Tile background' },
    { color: '#ffffe0', name: 'Title (light yellow)' },
    { color: '#ffffff', name: 'White' },
    { color: '#c0c0c0', name: 'Silver' },
    { color: '#808080', name: 'Grey' },
    { color: '#3a3a3a', name: 'Dark grey' },
    { color: '#000000', name: 'Black' },
    { color: '#ff0000', name: 'Red' },
    { color: '#ff8c00', name: 'Orange' },
    { color: '#ffff00', name: 'Yellow' },
    { color: '#00ff00', name: 'Green' },
    { color: '#2e8b57', name: 'Sea green' },
    { color: '#00ffff', name: 'Cyan' },
    { color: '#1e90ff', name: 'Blue' },
    { color: '#0000cd', name: 'Dark blue' },
    { color: '#8a2be2', name: 'Violet' },
    { color: '#ff69b4', name: 'Pink' },
    { color: '#8b4513', name: 'Brown' },
];

/** The colours a dashboard uses anywhere (tiles' options and colours, its defaults), each once, in the order found. */
export function colorsUsedIn(value: unknown) : string[] {
    const found = new Set<string>();
    const walk = (item: unknown) : void => {
        if (typeof item === 'string') {
            if (COLOR.test(item)) {
                found.add(item.toLowerCase());
            }
        } else if (Array.isArray(item)) {
            for (const each of item) {
                walk(each);
            }
        } else if (typeof item === 'object' && item !== null) {
            for (const each of Object.values(item)) {
                walk(each);
            }
        }
    };
    walk(value);

    return [...found];
}

function Swatch({ color, size = 5 }: { color: string | undefined; size?: number }) : ReactNode {
    return (
        <Box
            width={size}
            height={size}
            flex="none"
            borderRadius="sm"
            borderWidth="1px"
            borderColor="border"
            style={{ background: color ? cssColor(color) : 'repeating-linear-gradient(45deg, #3a3a3a 0 3px, transparent 3px 6px)' }}
            data-swatch={color ?? 'none'}
        />
    );
}

/** Swatches to tap, each with its colour as a title. */
function SwatchGrid({ colors, chosen, onPick }: { colors: readonly { color: string; name?: string }[]; chosen: string | undefined; onPick: (color: string) => void }) : ReactNode {
    return (
        <SimpleGrid minChildWidth="48px" gap="2">
            {colors.map(({ color, name }) => (
                <Button
                    key={color}
                    aria-label={name ? `${name} ${color}` : color}
                    title={name ? `${name} ${color}` : color}
                    variant="outline"
                    height="12"
                    p="1"
                    borderColor={color.toLowerCase() === chosen?.toLowerCase() ? 'blue.400' : 'border'}
                    borderWidth={color.toLowerCase() === chosen?.toLowerCase() ? '2px' : '1px'}
                    onClick={() => onPick(color)}
                >
                    <Box width="100%" height="100%" borderRadius="sm" style={{ background: cssColor(color) }} />
                </Button>
            ))}
        </SimpleGrid>
    );
}

export interface ColorFieldProps {
    label: string;
    description?: string | undefined;
    value: string | undefined;
    onChange: (value: string | undefined) => void;
    /** What the colour is when none is set: shown, and offered as "Default". */
    fallback?: string | undefined;
    /** The colours this dashboard already uses. */
    known?: readonly string[];
    /** A value meaning "none" to offer beside the default, where the default is a colour (issues #11, #12: `NONE`). */
    none?: string | undefined;
    /** What the default is called, where it is not one colour to show (a gauge's track: each style has its own). */
    defaultName?: string | undefined;
}

/** Whether a colour field's schema takes `"none"` beside a colour (`ColorOrNoneSchema`). */
export function allowsNone(schema: Readonly<Record<string, unknown>>) : boolean {
    return Array.isArray(schema.anyOf) && schema.anyOf.some((each: unknown) => typeof each === 'object' && each !== null && (each as { const?: unknown }).const === NONE);
}

/**
 * What leaving the colour out is called: its default, also where the default is none (a border, a
 * title or icon background). There "None" alone read as the explicit none, so a tile that had set
 * its own colour seemed to have no way back to the default. `named` is the field's own name for it.
 */
export function defaultLabelOf(fallback: string | undefined, none: string | undefined, named?: string) : string {
    if (named !== undefined) {
        return `Default (${named.charAt(0).toLowerCase()}${named.slice(1)})`;
    }

    if (fallback) {
        return `Default (${fallback})`;
    }

    return none === undefined ? 'None' : 'Default (none)';
}

/** A colour (T-2): a row with its swatch, opening the palette. */
export function ColorField({ label, description, value, onChange, fallback, known = [], none, defaultName }: ColorFieldProps) : ReactNode {
    const [isOpen, setOpen] = useState(false);
    const pick = (color: string | undefined) : void => {
        onChange(color);
        setOpen(false);
    };
    const isNone = none !== undefined && value === none;
    const shown = isNone ? undefined : (value ?? fallback);
    const defaultLabel = defaultLabelOf(fallback, none, defaultName);

    return (
        <ScreenRow
            label={label}
            value={
                <Box display="inline-flex" alignItems="center" gap="2">
                    <Swatch color={shown} />
                    <Text truncate>{isNone ? 'None' : (value ?? defaultLabel)}</Text>
                </Box>
            }
            isOpen={isOpen}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            screen={
                <Stack gap="5">
                    {description ? (
                        <Text fontSize="sm" color="fg.muted" px="1">
                            {description}
                        </Text>
                    ) : null}
                    <SettingsGroup>
                        <SettingsRow
                            title={
                                <Box as="span" display="inline-flex" alignItems="center" gap="2">
                                    <Swatch color={fallback} />
                                    {defaultLabel}
                                </Box>
                            }
                            isCurrent={value === undefined}
                            opens={false}
                            onClick={() => pick(undefined)}
                        />
                        {none !== undefined && (fallback || defaultName !== undefined) ? (
                            <SettingsRow
                                title={
                                    <Box as="span" display="inline-flex" alignItems="center" gap="2">
                                        <Swatch color={undefined} />
                                        None
                                    </Box>
                                }
                                isCurrent={isNone}
                                opens={false}
                                onClick={() => pick(none)}
                                data-testid={`${label.toLowerCase()}-none`}
                            />
                        ) : null}
                    </SettingsGroup>
                    {known.length > 0 ? (
                        <SettingsGroup title="In this dashboard">
                            <Box p="3">
                                <SwatchGrid colors={known.map((color) => ({ color }))} chosen={value} onPick={pick} />
                            </Box>
                        </SettingsGroup>
                    ) : null}
                    <SettingsGroup title="Palette">
                        <Box p="3">
                            <SwatchGrid colors={PALETTE} chosen={value} onPick={pick} />
                        </Box>
                    </SettingsGroup>
                    <SettingsGroup title="Your own" note="As #rrggbb, or #aarrggbb with its opacity first.">
                        <TextFieldRow
                            label="Colour"
                            value={isNone ? undefined : value}
                            placeholder="#rrggbb"
                            swatch={value ?? 'transparent'}
                            onChange={(typed) => {
                                if (typed === undefined || COLOR.test(typed)) {
                                    onChange(typed);
                                }
                            }}
                        />
                    </SettingsGroup>
                </Stack>
            }
        />
    );
}

/** A strip of a gradient's colours, for its row. */
function GradientStrip({ colors }: { colors: readonly string[] }) : ReactNode {
    const stops = colors.length <= 16 ? colors : Array.from({ length: 16 }, (_, index) => colors[Math.floor((index * colors.length) / 16)]!);

    return <Box width="24" height="3" flex="none" borderRadius="sm" style={{ background: `linear-gradient(to right, ${stops.join(', ')})` }} data-gradient />;
}

export interface GradientFieldProps {
    label: string;
    description?: string | undefined;
    /** A gradient's name, or a list of colours. */
    value: string | readonly string[] | undefined;
    onChange: (value: string | string[] | undefined) => void;
    fallback?: string | undefined;
}

/** A gauge's colours: a named gradient, or a list of one's own (`#ff0000, #00ff00`). */
export function GradientField({ label, description, value, onChange, fallback }: GradientFieldProps) : ReactNode {
    const [isOpen, setOpen] = useState(false);
    const chosen = typeof value === 'string' ? value : value === undefined ? fallback : undefined;
    const colorsOf = (name: string) : string[] => {
        try {
            return namedGradient(name);
        } catch {
            // A name the gauges do not know shows no strip; the tile says so when it draws.
            return [];
        }
    };
    const list = Array.isArray(value) ? value.join(', ') : undefined;

    return (
        <ScreenRow
            label={label}
            value={
                <Box display="inline-flex" alignItems="center" gap="2">
                    <GradientStrip colors={Array.isArray(value) ? value : chosen ? colorsOf(chosen) : []} />
                    <Text truncate>{Array.isArray(value) ? list : (typeof value === 'string' ? value : fallback ? `Default (${fallback})` : 'None')}</Text>
                </Box>
            }
            isOpen={isOpen}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            screen={
                <Stack gap="5">
                    {description ? (
                        <Text fontSize="sm" color="fg.muted" px="1">
                            {description}
                        </Text>
                    ) : null}
                    <SettingsGroup title="Gradients">
                        {GRADIENT_NAMES.map((name) => (
                            <SettingsRow
                                key={name}
                                title={
                                    <Box as="span" display="inline-flex" alignItems="center" gap="3">
                                        <GradientStrip colors={colorsOf(name)} />
                                        {name === fallback ? `${name} (default)` : name}
                                    </Box>
                                }
                                isCurrent={name === chosen && !Array.isArray(value)}
                                opens={false}
                                onClick={() => {
                                    onChange(name === fallback ? undefined : name);
                                    setOpen(false);
                                }}
                            />
                        ))}
                    </SettingsGroup>
                    <SettingsGroup title="Your own" note="Two or more colours, as #rrggbb, separated by commas.">
                        <TextFieldRow
                            label="Colours"
                            value={list}
                            placeholder="#ff0000, #00ff00"
                            onChange={(typed) => {
                                const colors = (typed ?? '').split(',').map((part) => part.trim()).filter((part) => part !== '');
                                if (colors.length >= 2 && colors.every((color) => COLOR.test(color))) {
                                    onChange(colors);
                                } else if (typed === undefined) {
                                    onChange(undefined);
                                }
                            }}
                        />
                    </SettingsGroup>
                </Stack>
            }
        />
    );
}
